/**
 * milestoneEditorOrchestrator — Module-level singleton that routes
 * "open milestone editor" requests to the currently active host page.
 *
 * Framework-agnostic: no React dependency. The React bridge
 * (MilestoneEditorContext) calls `setNavigationFallback` once to
 * supply router-based navigation for the no-host fallback path.
 *
 * Usage from any component / service:
 *   import { openMilestoneEditor } from '@/services/milestoneEditorOrchestrator';
 *   openMilestoneEditor({ milestoneId, initialTab: 'governance', source: 'capital_gate' });
 */

import type {
    OpenMilestoneEditorRequest,
    MilestoneEditorHost,
    MilestoneEditorHostType,
    MilestoneEditorSource,
    EditorClosedListener,
    MilestoneEditorSession,
    MilestoneEditorEvent,
    MilestoneEditorEventListener,
} from '@/types/milestoneEditor';

// ── Host registry ────────────────────────────────────────────────────────────

const hosts = new Set<MilestoneEditorHost>();

/**
 * Register a host page. Returns an unregister function.
 */
export function registerMilestoneEditorHost(host: MilestoneEditorHost): () => void {
    hosts.add(host);
    return () => { hosts.delete(host); };
}

/** Remove a specific host (alternative to calling the unregister fn). */
export function unregisterMilestoneEditorHost(host: MilestoneEditorHost): void {
    hosts.delete(host);
}

// ── Active session (lifecycle-aware) ─────────────────────────────────────────

let activeSession: MilestoneEditorSession | null = null;

/** Get the currently active editor session (or null). */
export function getActiveMilestoneEditorSession(): MilestoneEditorSession | null {
    return activeSession;
}

// ── Global event subscription system ─────────────────────────────────────────

const eventListeners = new Set<MilestoneEditorEventListener>();

/** Subscribe to all milestone editor lifecycle events. Returns an unsubscribe fn. */
export function subscribeToMilestoneEditorEvents(listener: MilestoneEditorEventListener): () => void {
    eventListeners.add(listener);
    return () => { eventListeners.delete(listener); };
}

function emitEvent(session: MilestoneEditorSession, type: MilestoneEditorEvent['type'], extra?: { duration?: number }): void {
    const event: MilestoneEditorEvent = { type, session, timestamp: Date.now(), ...extra };
    logMilestoneEditorEvent(event);
    for (const listener of eventListeners) {
        listener(event);
    }
}

// ── Editor-closed listeners (legacy) ─────────────────────────────────────────

const closedListeners = new Set<EditorClosedListener>();

/** Subscribe to editor-closed events. Returns an unsubscribe function. */
export function onEditorClosed(listener: EditorClosedListener): () => void {
    closedListeners.add(listener);
    return () => { closedListeners.delete(listener); };
}

// ── Navigation fallback ──────────────────────────────────────────────────────

type NavigateFn = (path: string) => void;
let navigateFn: NavigateFn | null = null;

/** Called once by the React bridge to supply router navigation. */
export function setNavigationFallback(fn: NavigateFn): void {
    navigateFn = fn;
}

function fallbackNavigateToMilestones(request: OpenMilestoneEditorRequest): void {
    const step = request.initialTab === 'requirements' ? 'verification' : 'overview';
    const url = `/milestones/${encodeURIComponent(request.milestoneId)}/governance#${step}`;

    if (navigateFn) {
        navigateFn(url);
    } else {
        window.location.href = url;
    }
}

// ── Host priority resolution ─────────────────────────────────────────────────

/**
 * Source → preferred host type mapping.
 * If the preferred host is not mounted, any available host is tried.
 */
const SOURCE_HOST_PREFERENCE: Partial<Record<MilestoneEditorSource, MilestoneEditorHostType>> = {
    capital_gate: 'project_detail',
    project_overview: 'project_detail',
    milestone_card: 'milestones',
    timeline: 'milestones',
};

function resolveHostOrder(source?: MilestoneEditorSource): MilestoneEditorHost[] {
    const arr = Array.from(hosts);
    if (!source) return arr;

    const preferred = SOURCE_HOST_PREFERENCE[source];
    if (!preferred) return arr;

    // Partition: preferred type first, then the rest
    const primary: MilestoneEditorHost[] = [];
    const secondary: MilestoneEditorHost[] = [];
    for (const h of arr) {
        (h.type === preferred ? primary : secondary).push(h);
    }
    return [...primary, ...secondary];
}

// ── Session identity ─────────────────────────────────────────────────────────

let sessionCounter = 0;
function generateSessionId(): string {
    sessionCounter += 1;
    return `mse-${Date.now()}-${sessionCounter}`;
}

// ── Close timer ──────────────────────────────────────────────────────────────

let closeTimer: ReturnType<typeof setTimeout> | null = null;

function clearCloseTimer(): void {
    if (closeTimer !== null) {
        clearTimeout(closeTimer);
        closeTimer = null;
    }
}

// ── System logging ───────────────────────────────────────────────────────────

function logMilestoneEditorEvent(event: MilestoneEditorEvent): void {
    const { session, type, duration } = event;
    const tag = `[MilestoneEditor:${type}]`;
    const info: Record<string, unknown> = {
        sessionId: session.id,
        milestoneId: session.request.milestoneId,
        source: session.request.source ?? 'unknown',
    };
    if (duration != null) info.duration = `${duration}ms`;
    console.debug(tag, info);
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Open the milestone editor from *any* surface.
 *
 * Idempotency:
 *  - If the same milestoneId is already open and not closing → no-op.
 *  - If a *different* milestoneId is open → auto-close previous, then open new.
 *
 * Resolves the best host using source-based priority, then falls back to the
 * canonical milestone governance workspace.
 */
export function openMilestoneEditor(request: OpenMilestoneEditorRequest): void {
    // ── Idempotent open guard ────────────────────────────────────────────
    if (
        activeSession &&
        activeSession.state !== 'closing' &&
        activeSession.request.milestoneId === request.milestoneId
    ) {
        return; // already open for this milestone — no-op
    }

    // ── Cancel previous session if different milestone ───────────────────
    if (activeSession && activeSession.state !== 'closing') {
        performClose(activeSession);
    }
    clearCloseTimer();

    // ── Create new session ───────────────────────────────────────────────
    const now = Date.now();
    const session: MilestoneEditorSession = {
        id: generateSessionId(),
        request,
        state: 'opening',
        openedAt: now,
    };
    activeSession = session;

    const ordered = resolveHostOrder(request.source);
    for (const host of ordered) {
        if (host.open(request)) {
            activeSession = { ...session, state: 'active' };
            emitEvent(activeSession, 'opened');
            return;
        }
    }

    // Fallback — still mark active (editor will open on the target page)
    activeSession = { ...session, state: 'active' };
    emitEvent(activeSession, 'opened');
    fallbackNavigateToMilestones(request);
}

/**
 * Close the active editor session and notify listeners + host.
 *
 * Safe guards:
 *  - No-op if no active session.
 *  - No-op if already closing (prevents double-close).
 *  - 150 ms delay before clearing session (animation compatibility).
 */
export function closeMilestoneEditor(): void {
    if (!activeSession) return;
    if (activeSession.state === 'closing') return;

    const closingSession: MilestoneEditorSession = { ...activeSession, state: 'closing' };
    activeSession = closingSession;

    performClose(closingSession);

    // Delay session clear for animation compatibility (~150ms)
    clearCloseTimer();
    closeTimer = setTimeout(() => {
        // Only clear if the session hasn't been replaced by a new open()
        if (activeSession?.id === closingSession.id) {
            activeSession = null;
        }
        closeTimer = null;
    }, 150);
}

/** Internal close mechanics — notify hosts, legacy listeners, emit event. */
function performClose(session: MilestoneEditorSession): void {
    const duration = Date.now() - session.openedAt;

    // Notify hosts
    for (const host of hosts) {
        host.onEditorClosed?.(session.request);
    }

    // Notify legacy closed listeners
    for (const listener of closedListeners) {
        listener(session.request);
    }

    // Emit event with duration
    emitEvent(session, 'closed', { duration });
}
